import { useCallback } from 'react';
import { core_v1_DataSource } from '@antimatterhq/asl-api-ts-v1';
import { Button } from '@antimatterhq/ui/components/button';
import { Icon } from '@antimatterhq/ui/components/icon';
import { Text } from '@antimatterhq/ui/components/typography';
import toast, { Toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

export type DatasourceUpgradeToastOptions = {
  presetName: string;
  presetVersion: number;
  datasource: core_v1_DataSource;
};

const DatasourceUpgradeToast = ({
  presetName,
  presetVersion,
  datasource,
  t,
}: DatasourceUpgradeToastOptions & { t: Toast }) => {
  const navigate = useNavigate();

  const handleContinueReview = useCallback(() => {
    toast.dismiss(t.id);
    navigate(
      `/configure/data-sources/upgrade/${datasource.metadata?.name}?upgradePresetVersion=${presetVersion}`,
    );
  }, [datasource, presetVersion, navigate, t]);

  return (
    <div className="flex w-[100%] items-center justify-between">
      <div className="flex items-start gap-2">
        <Icon icon="radix-icons:update" className="h-4 w-4 text-yellow-600" />
        <div className="flex flex-col gap-1">
          <Text
            bold
            className="text-yellow-600 dark:text-yellow-600"
          >{`Preset upgrade: ${presetName} v${presetVersion}`}</Text>
          <Text className="text-yellow-600 dark:text-yellow-600">
            No edits can be made to this preset while review is pending.
          </Text>
        </div>
      </div>
      <Button onClick={handleContinueReview} variant="warning">
        Continue review
      </Button>
    </div>
  );
};

export const useDatasourceUpgradeToast = () => {
  const showDatasourceUpgradeInProgress = ({
    presetName,
    presetVersion,
    datasource,
  }: DatasourceUpgradeToastOptions) => {
    toast(
      (t) => (
        <DatasourceUpgradeToast
          presetName={presetName}
          presetVersion={presetVersion}
          datasource={datasource}
          t={t}
        />
      ),
      {
        duration: 5000,
        className:
          'min-w-[700px] rounded-md border border-yellow-600 bg-yellow-300',
      },
    );
  };

  return {
    showDatasourceUpgradeInProgress,
  };
};
